/**
 * ====================================================================
 * FIKO CAD DIMENSION MANAGER
 * ====================================================================
 *
 * CATIA/IronCAD/SolidWorks seviyesinde profesyonel ölçülendirme yöneticisi.
 * Tüm dimension tiplerini oluşturur, yönetir ve koordine eder.
 *
 * v2.0.0 Yenilikler:
 * - DimensionSettings entegrasyonu (preset'ler, konfigürasyon)
 * - DimensionAnimator (fade, pulse, snap animasyonları)
 * - DimensionTooltip (hover bilgi paneli)
 * - SmartLabelPlacer (akıllı label yerleştirme)
 * - DimensionGlowEffect (selection glow efekti)
 *
 * API:
 * - createLinearDimension(refA, refB, options)
 * - createAxisDimension(refA, refB, axisLock, options)
 * - createAngleDimension(refA, refB, options)
 * - createDiameterDimension(ref, options)
 * - createRadiusDimension(ref, options)
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 * @license Commercial - Satılabilir Kalite
 */

import * as THREE from 'three';
import { FikoSahne } from '../../FikoSahne';
import {
    Dimension,
    DimensionType,
    DimensionResult,
    DimensionError,
    DimensionErrorType,
    DimensionState,
    GeometryReference,
    ReferenceType,
    AxisLock,
    LinearDimensionOptions,
    AngularDimensionOptions,
    DiameterDimensionOptions,
    BaseDimensionOptions,
    DIMENSION_CONSTANTS
} from './DimensionTypes';
import { LinearDimension } from './LinearDimension';
import { AngularDimension } from './AngleDimension';
import { DiameterDimension } from './DiameterDimension';
import { RadiusDimension } from './RadiusDimension';
import { CoordinateDimension, CoordinateDimensionOptions } from './CoordinateDimension';
import { LayoutManager } from './LayoutManager';
import { FallbackResolver } from './FallbackResolver';
import { DraggableSupport } from './DraggableSupport';

// v2.0.0 - New Visual System imports
import { DimensionSettings, SettingsChangeEvent, type PresetName } from './DimensionSettings';
import { DimensionAnimator } from './visual/DimensionAnimator';
import { DimensionTooltip } from './visual/DimensionTooltip';
import { DimensionGlowEffect } from './visual/DimensionGlowEffect';
import { SmartLabelPlacer } from './layout/SmartLabelPlacer';

/**
 * Dimension Manager - Ana kontrol sınıfı
 */
export class DimensionManager {
    // Sahne referansı
    private scene: FikoSahne;

    // Tüm dimension'lar
    private dimensions: Map<string, Dimension> = new Map();

    // Alt yöneticiler
    private layoutManager: LayoutManager;
    private fallbackResolver: FallbackResolver;
    private draggableSupport: DraggableSupport;

    // Container group
    private container: THREE.Group;

    // Event handlers
    private eventHandlers: Map<string, Set<Function>> = new Map();

    // Performans sayaçları
    private updateCount: number = 0;
    private lastUpdateTime: number = 0;

    // Kamera değişiklik izleme
    private cameraObserver: (() => void) | null = null;

    // ====================================================================
    // v2.0.0 - VISUAL SYSTEM
    // ====================================================================

    // Settings manager (singleton)
    private settings: DimensionSettings;

    // Animator (singleton)
    private animator: DimensionAnimator;

    // Tooltip manager (singleton)
    private tooltip: DimensionTooltip;

    // Smart label placer
    private labelPlacer: SmartLabelPlacer;

    // Glow effect manager
    private glowEffect: DimensionGlowEffect;

    // Hover/interaction tracking
    private hoveredDimension: Dimension | null = null;
    private isInteractionEnabled: boolean = true;

    constructor(scene: FikoSahne) {
        this.scene = scene;

        // Container oluştur
        this.container = new THREE.Group();
        this.container.name = 'DimensionManager_Container';
        this.container.renderOrder = Infinity;
        this.container.frustumCulled = false;

        // 🎯 Sahneye ekle - FikoSahne.ekle() kullan (hazirla() çağrılır)
        if (scene) {
            // FikoSahne.ekle() metodu varsa kullan, yoksa THREE.js scene.add()
            if (typeof scene.ekle === 'function') {
                scene.ekle(this.container);
            } else if (scene.scene) {
                scene.scene.add(this.container);
            }
        }

        // Alt yöneticileri başlat
        this.layoutManager = new LayoutManager(this);
        this.fallbackResolver = new FallbackResolver(this);
        this.draggableSupport = new DraggableSupport(this);

        // v2.0.0 - Visual System initialization
        this.settings = DimensionSettings.getInstance();
        this.animator = DimensionAnimator.getInstance();
        this.tooltip = DimensionTooltip.getInstance();
        this.labelPlacer = new SmartLabelPlacer();
        this.glowEffect = new DimensionGlowEffect();

        // Settings değişikliklerini dinle
        this.settings.addChangeListener(this.onSettingsChanged.bind(this));
        // console.log('[DimensionManager] Created and listener added');

        // Kamera değişikliklerini dinle
        this.setupCameraObserver();

        // 🎯 Sürükleme desteği DEVRE DIŞI - Kullanıcı kontrolüne bırakıldı
        // Sürükleme istenirse: manager.enableDragSupport(canvas) çağrılmalı
        // this.initializeDragSupport();
    }

    /**
     * 🎯 Sürükleme desteğini canvas'a bağla
     */
    private initializeDragSupport(): void {
        // Canvas'a erişim için küçük bir gecikme (DOM hazır olsun)
        setTimeout(() => {
            const canvas = this.scene?.anaGosterim?.sunucu?.domElement;
            if (canvas) {
                this.draggableSupport.attach(canvas);
            }
        }, 100);
    }

    /**
     * 🎯 Sürükleme desteğini manuel olarak başlat
     * (Eğer constructor'da canvas hazır değilse)
     */
    enableDragSupport(canvas: HTMLElement): void {
        this.draggableSupport.attach(canvas);
    }

    /**
     * 🎯 Sürükleme desteğini devre dışı bırak
     */
    disableDragSupport(): void {
        this.draggableSupport.detach();
    }

    /**
     * 🎯 Sürükleme aktif mi kontrol et
     * Bu metod kumpas/cetvel gibi bileşenlerin tıklama olaylarında
     * sürükleme sırasında temizleme yapmamasını sağlar
     */
    isDragging(): boolean {
        return this.draggableSupport?.isDragging() ?? false;
    }

    /**
     * 🎯 Bir dimension üzerinde hover var mı kontrol et
     */
    isHovering(): boolean {
        return this.draggableSupport?.isHovering() ?? false;
    }

    // ====================================================================
    // PUBLIC API - DIMENSION CREATION
    // ====================================================================

    /**
     * Linear Dimension oluştur - İki nokta arası düz mesafe
     *
     * @param refA İlk referans noktası/kenar
     * @param refB İkinci referans noktası/kenar
     * @param options Dimension seçenekleri
     * @returns DimensionResult
     */
    createLinearDimension(
        refA: GeometryReference,
        refB: GeometryReference,
        options: LinearDimensionOptions = {}
    ): DimensionResult {
        try {
            // Validation
            const validation = this.validateTwoPointReferences(refA, refB);
            if (!validation.valid) {
                return this.createErrorResult(validation.error!);
            }

            // Dimension oluştur
            const dimension = new LinearDimension(
                refA,
                refB,
                DimensionType.LINEAR,
                options,
                this.scene
            );

            // Oluştur ve validate et
            const result = dimension.create();
            if (!result.success) {
                return result;
            }

            // Layout kontrolü
            const layoutResult = this.layoutManager.checkAndResolve(dimension);
            if (layoutResult.fallback) {
                result.fallback = layoutResult.fallback;
            }

            // Kaydet ve sahneye ekle
            this.addDimension(dimension);

            return result;
        } catch (error) {
            return this.createErrorResult({
                type: DimensionErrorType.INTERNAL_ERROR,
                message: `Linear dimension oluşturma hatası: ${error}`,
                details: error
            });
        }
    }

    /**
     * Axis-Locked Dimension oluştur - Horizontal/Vertical ölçü
     *
     * @param refA İlk referans noktası
     * @param refB İkinci referans noktası
     * @param axisLock Kilitlenecek eksen (X=horizontal, Y=vertical)
     * @param options Dimension seçenekleri
     * @returns DimensionResult
     */
    createAxisDimension(
        refA: GeometryReference,
        refB: GeometryReference,
        axisLock: AxisLock,
        options: LinearDimensionOptions = {}
    ): DimensionResult {
        try {
            // Validation
            const validation = this.validateTwoPointReferences(refA, refB);
            if (!validation.valid) {
                return this.createErrorResult(validation.error!);
            }

            // Axis lock kontrolü
            if (axisLock === AxisLock.NONE) {
                return this.createLinearDimension(refA, refB, options);
            }

            // Dimension tipini belirle
            const dimensionType = axisLock === AxisLock.X
                ? DimensionType.HORIZONTAL
                : axisLock === AxisLock.Y
                    ? DimensionType.VERTICAL
                    : DimensionType.ALIGNED;

            // Dimension oluştur
            const dimension = new LinearDimension(
                refA,
                refB,
                dimensionType,
                { ...options, axisLock },
                this.scene
            );

            // Oluştur ve validate et
            const result = dimension.create();
            if (!result.success) {
                return result;
            }

            // Layout kontrolü
            const layoutResult = this.layoutManager.checkAndResolve(dimension);
            if (layoutResult.fallback) {
                result.fallback = layoutResult.fallback;
            }

            // Kaydet ve sahneye ekle
            this.addDimension(dimension);

            return result;
        } catch (error) {
            return this.createErrorResult({
                type: DimensionErrorType.INTERNAL_ERROR,
                message: `Axis dimension oluşturma hatası: ${error}`,
                details: error
            });
        }
    }

    /**
     * Angular Dimension oluştur - Açı ölçüsü
     *
     * @param refA İlk kenar/çizgi referansı
     * @param refB İkinci kenar/çizgi referansı (veya açı merkezi)
     * @param options Angular dimension seçenekleri
     * @returns DimensionResult
     */
    createAngleDimension(
        refA: GeometryReference,
        refB: GeometryReference,
        options: AngularDimensionOptions = {}
    ): DimensionResult {
        try {
            // Validation
            const validation = this.validateAngleReferences(refA, refB);
            if (!validation.valid) {
                return this.createErrorResult(validation.error!);
            }

            // Dimension oluştur
            const dimension = new AngularDimension(
                refA,
                refB,
                options,
                this.scene
            );

            // Oluştur ve validate et
            const result = dimension.create();
            if (!result.success) {
                return result;
            }

            // Layout kontrolü
            const layoutResult = this.layoutManager.checkAndResolve(dimension);
            if (layoutResult.fallback) {
                result.fallback = layoutResult.fallback;
            }

            // Kaydet ve sahneye ekle
            this.addDimension(dimension);

            return result;
        } catch (error) {
            return this.createErrorResult({
                type: DimensionErrorType.INTERNAL_ERROR,
                message: `Angular dimension oluşturma hatası: ${error}`,
                details: error
            });
        }
    }

    /**
     * Diameter Dimension oluştur - Çap ölçüsü (⌀)
     *
     * @param ref Daire/silindir referansı
     * @param options Diameter dimension seçenekleri
     * @returns DimensionResult
     */
    createDiameterDimension(
        ref: GeometryReference,
        options: DiameterDimensionOptions = {}
    ): DimensionResult {
        try {
            // Validation
            const validation = this.validateCircularReference(ref);
            if (!validation.valid) {
                return this.createErrorResult(validation.error!);
            }

            // Dimension oluştur
            const dimension = new DiameterDimension(
                ref,
                { ...options, prefix: '⌀' },
                this.scene
            );

            // Oluştur ve validate et
            const result = dimension.create();
            if (!result.success) {
                return result;
            }

            // Layout kontrolü
            const layoutResult = this.layoutManager.checkAndResolve(dimension);
            if (layoutResult.fallback) {
                result.fallback = layoutResult.fallback;
            }

            // Kaydet ve sahneye ekle
            this.addDimension(dimension);

            return result;
        } catch (error) {
            return this.createErrorResult({
                type: DimensionErrorType.INTERNAL_ERROR,
                message: `Diameter dimension oluşturma hatası: ${error}`,
                details: error
            });
        }
    }

    /**
     * Radius Dimension oluştur - Yarıçap ölçüsü (R)
     *
     * @param ref Daire/silindir referansı
     * @param options Radius dimension seçenekleri
     * @returns DimensionResult
     */
    createRadiusDimension(
        ref: GeometryReference,
        options: DiameterDimensionOptions = {}
    ): DimensionResult {
        try {
            // Validation
            const validation = this.validateCircularReference(ref);
            if (!validation.valid) {
                return this.createErrorResult(validation.error!);
            }

            // Dimension oluştur
            const dimension = new RadiusDimension(
                ref,
                { ...options, prefix: 'R' },
                this.scene
            );

            // Oluştur ve validate et
            const result = dimension.create();
            if (!result.success) {
                return result;
            }

            // Layout kontrolü
            const layoutResult = this.layoutManager.checkAndResolve(dimension);
            if (layoutResult.fallback) {
                result.fallback = layoutResult.fallback;
            }

            // Kaydet ve sahneye ekle
            this.addDimension(dimension);

            return result;
        } catch (error) {
            return this.createErrorResult({
                type: DimensionErrorType.INTERNAL_ERROR,
                message: `Radius dimension oluşturma hatası: ${error}`,
                details: error
            });
        }
    }

    /**
     * 🎯 v2.1.0: Koordinat dimension oluştur
     * Nokta seçildiğinde X, Y, Z koordinatlarını 3D sahneye yazar
     */
    createCoordinateDimension(
        ref: GeometryReference,
        options: CoordinateDimensionOptions = {}
    ): DimensionResult {
        try {
            // Validation - nokta gerekli
            if (!ref.point) {
                return this.createErrorResult({
                    type: DimensionErrorType.INVALID_REFERENCE,
                    message: 'Koordinat ölçümü için nokta gerekli'
                });
            }

            // Dimension oluştur
            const dimension = new CoordinateDimension(
                ref,
                options,
                this.scene
            );

            // Oluştur ve validate et
            const result = dimension.create();
            if (!result.success) {
                return result;
            }

            // Kaydet ve sahneye ekle
            this.addDimension(dimension);

            return result;
        } catch (error) {
            return this.createErrorResult({
                type: DimensionErrorType.INTERNAL_ERROR,
                message: `Coordinate dimension oluşturma hatası: ${error}`,
                details: error
            });
        }
    }

    // ====================================================================
    // PUBLIC API - DIMENSION MANAGEMENT
    // ====================================================================

    /**
     * Dimension'ı ID ile getir
     */
    getDimension(id: string): Dimension | undefined {
        return this.dimensions.get(id);
    }

    /**
     * Tüm dimension'ları getir
     */
    getAllDimensions(): Dimension[] {
        return Array.from(this.dimensions.values());
    }

    /**
     * Dimension'ı sil
     */
    removeDimension(id: string): boolean {
        const dimension = this.dimensions.get(id);
        if (!dimension) return false;

        // Temizle
        dimension.dispose();
        this.container.remove(dimension);
        this.dimensions.delete(id);

        // Event emit
        this.emit('dimensionRemoved', { id });

        return true;
    }

    /**
     * Tüm dimension'ları sil
     */
    clearAll(): void {
        // console.log('[DimensionManager] clearAll called, clearing', this.dimensions.size, 'dimensions');
        this.dimensions.forEach((dimension, id) => {
            dimension.dispose();
            this.container.remove(dimension);
        });
        this.dimensions.clear();

        // Event emit
        this.emit('allDimensionsCleared', {});
    }

    /**
     * 🎯 Sadece belirli session'daki dimension'ları temizle
     * Kalıcı ölçümler (kaliciOlcumlerGrubu'na taşınanlar) korunur
     * @param sessionId Temizlenecek session ID
     */
    clearSession(sessionId: string): void {
        // console.log('[DimensionManager] clearSession called for:', sessionId, 'total dimensions:', this.dimensions.size);

        const toDelete: string[] = [];

        this.dimensions.forEach((dimension, id) => {
            // Session ID kontrolü - sadece bu session'a ait olanları sil
            const dimSessionId = dimension.userData?.sessionId;

            // 🎯 FIX: Sadece sessionId eşleşenleri sil
            // SessionId olmayan dimension'ları SİLME (farklı session olabilir)
            if (dimSessionId && dimSessionId === sessionId) {
                toDelete.push(id);
            }
        });

        // console.log('[DimensionManager] clearSession will delete:', toDelete.length, 'dimensions');

        // İşaretlenmiş ölçümleri sil
        toDelete.forEach(id => {
            const dimension = this.dimensions.get(id);
            if (dimension) {
                dimension.dispose();
                this.container.remove(dimension);
                this.dimensions.delete(id);
            }
        });

        // Event emit
        this.emit('sessionCleared', { sessionId, deletedCount: toDelete.length });
    }

    /**
     * Dimension'ları güncelle (kamera değişikliğinde)
     */
    updateAll(): void {
        const now = performance.now();

        // Throttle
        if (now - this.lastUpdateTime < DIMENSION_CONSTANTS.UPDATE_THROTTLE_MS) {
            return;
        }
        this.lastUpdateTime = now;

        // Tüm dimension'ları güncelle
        this.dimensions.forEach(dimension => {
            dimension.update();
        });

        this.updateCount++;
    }

    /**
     * Dimension'ı seç
     */
    selectDimension(id: string): boolean {
        const dimension = this.dimensions.get(id);
        if (!dimension) return false;

        // Önceki seçimi kaldır
        this.dimensions.forEach(d => {
            if (d.state === DimensionState.SELECTED) {
                d.state = DimensionState.NORMAL;
            }
        });

        // Yeni seçimi uygula
        dimension.state = DimensionState.SELECTED;

        // Event emit
        this.emit('dimensionSelected', { id, dimension });

        return true;
    }

    /**
     * Seçimi kaldır
     */
    deselectAll(): void {
        this.dimensions.forEach(dimension => {
            if (dimension.state === DimensionState.SELECTED) {
                dimension.state = DimensionState.NORMAL;
            }
        });

        // Event emit
        this.emit('allDeselected', {});
    }

    // ====================================================================
    // EVENT SYSTEM
    // ====================================================================

    /**
     * Event dinleyici ekle
     */
    on(event: string, handler: Function): void {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, new Set());
        }
        this.eventHandlers.get(event)!.add(handler);
    }

    /**
     * Event dinleyici kaldır
     */
    off(event: string, handler: Function): void {
        const handlers = this.eventHandlers.get(event);
        if (handlers) {
            handlers.delete(handler);
        }
    }

    /**
     * Event emit
     */
    private emit(event: string, data: any): void {
        const handlers = this.eventHandlers.get(event);
        if (handlers) {
            handlers.forEach(handler => {
                try {
                    handler(data);
                } catch (e) {
                    // Event handler error - silently ignore
                }
            });
        }
    }

    // ====================================================================
    // VALIDATION HELPERS
    // ====================================================================

    /**
     * İki nokta referansını validate et
     */
    private validateTwoPointReferences(
        refA: GeometryReference,
        refB: GeometryReference
    ): { valid: boolean; error?: DimensionError } {
        // Referans kontrolü
        if (!refA || !refB) {
            return {
                valid: false,
                error: {
                    type: DimensionErrorType.INVALID_REFERENCE,
                    message: 'Referanslar tanımlı değil'
                }
            };
        }

        // Nokta pozisyonları
        const pointA = this.getPointFromReference(refA);
        const pointB = this.getPointFromReference(refB);

        if (!pointA || !pointB) {
            return {
                valid: false,
                error: {
                    type: DimensionErrorType.INVALID_REFERENCE,
                    message: 'Referans noktaları hesaplanamadı'
                }
            };
        }

        // Çakışık nokta kontrolü
        const distance = pointA.distanceTo(pointB);
        if (distance < DIMENSION_CONSTANTS.MIN_DISTANCE) {
            return {
                valid: false,
                error: {
                    type: DimensionErrorType.COINCIDENT_POINTS,
                    message: `Noktalar çakışık (mesafe: ${distance.toFixed(6)}mm)`
                }
            };
        }

        return { valid: true };
    }

    /**
     * Açı referanslarını validate et
     */
    private validateAngleReferences(
        refA: GeometryReference,
        refB: GeometryReference
    ): { valid: boolean; error?: DimensionError } {
        // Temel referans kontrolü
        if (!refA || !refB) {
            return {
                valid: false,
                error: {
                    type: DimensionErrorType.INVALID_REFERENCE,
                    message: 'Açı referansları tanımlı değil'
                }
            };
        }

        // Yön vektörleri kontrolü (kenar referansları için)
        if (refA.type === ReferenceType.EDGE && refB.type === ReferenceType.EDGE) {
            if (!refA.direction || !refB.direction) {
                return {
                    valid: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Kenar yönleri tanımlı değil'
                    }
                };
            }

            // Paralel çizgi kontrolü
            const dot = Math.abs(refA.direction.dot(refB.direction));
            if (dot > 0.9999) {
                return {
                    valid: false,
                    error: {
                        type: DimensionErrorType.PARALLEL_LINES,
                        message: 'Çizgiler paralel, açı ölçülemez'
                    }
                };
            }
        }

        return { valid: true };
    }

    /**
     * Dairesel referansı validate et
     */
    private validateCircularReference(
        ref: GeometryReference
    ): { valid: boolean; error?: DimensionError } {
        // Referans kontrolü
        if (!ref) {
            return {
                valid: false,
                error: {
                    type: DimensionErrorType.INVALID_REFERENCE,
                    message: 'Referans tanımlı değil'
                }
            };
        }

        // Tip kontrolü
        const validTypes = [
            ReferenceType.CIRCLE,
            ReferenceType.ARC,
            ReferenceType.CYLINDER,
            ReferenceType.SPHERE
        ];

        if (!validTypes.includes(ref.type)) {
            return {
                valid: false,
                error: {
                    type: DimensionErrorType.INVALID_REFERENCE,
                    message: `Geçersiz referans tipi: ${ref.type}. Daire, yay, silindir veya küre olmalı.`
                }
            };
        }

        // Yarıçap kontrolü
        if (!ref.radius || ref.radius < DIMENSION_CONSTANTS.MIN_RADIUS) {
            return {
                valid: false,
                error: {
                    type: DimensionErrorType.INVALID_REFERENCE,
                    message: `Geçersiz yarıçap: ${ref.radius}`
                }
            };
        }

        // Merkez kontrolü
        if (!ref.center) {
            return {
                valid: false,
                error: {
                    type: DimensionErrorType.INVALID_REFERENCE,
                    message: 'Merkez noktası tanımlı değil'
                }
            };
        }

        return { valid: true };
    }

    /**
     * Referanstan nokta pozisyonu al
     */
    private getPointFromReference(ref: GeometryReference): THREE.Vector3 | null {
        if (ref.point) {
            return ref.point.clone();
        }

        if (ref.center) {
            return ref.center.clone();
        }

        // Mesh'ten hesapla
        if (ref.mesh && ref.vertexIndex !== undefined) {
            const positions = ref.mesh.geometry.getAttribute('position');
            if (positions) {
                const point = new THREE.Vector3();
                point.fromBufferAttribute(positions, ref.vertexIndex);
                point.applyMatrix4(ref.mesh.matrixWorld);
                return point;
            }
        }

        return null;
    }

    // ====================================================================
    // INTERNAL HELPERS
    // ====================================================================

    /**
     * Dimension'ı kaydet ve sahneye ekle
     */
    private addDimension(dimension: Dimension): void {
        const id = dimension.userData.dimensionId;
        this.dimensions.set(id, dimension);
        this.container.add(dimension);
        // console.log('[DimensionManager] Dimension added, total:', this.dimensions.size);

        // Event emit
        this.emit('dimensionAdded', { id, dimension });
    }

    /**
     * Hata sonucu oluştur
     */
    private createErrorResult(error: DimensionError): DimensionResult {
        return {
            success: false,
            error
        };
    }

    /**
     * Kamera değişiklik izleyici kur
     */
    private setupCameraObserver(): void {
        if (!this.scene || !this.scene.anaGosterim) return;

        // Kamera değişikliğinde dimension'ları güncelle
        this.cameraObserver = () => {
            this.updateAll();
        };

        // FikoSahne event sistemine bağlan
        // Not: kontrolDegisti$ yerine OrbitControls 'change' event'i kullanılabilir
        // veya manuel updateAll() çağrısı yapılabilir
        // Şimdilik sadece requestAnimationFrame ile güncelle
        const animate = () => {
            if (this.cameraObserver) {
                this.cameraObserver();
                requestAnimationFrame(animate);
            }
        };
        // Başlangıçta animasyon döngüsü başlatma (performans için devre dışı)
        // requestAnimationFrame(animate);
    }

    /**
     * Sahne referansı
     */
    getScene(): FikoSahne {
        return this.scene;
    }

    /**
     * Container referansı
     */
    getContainer(): THREE.Group {
        return this.container;
    }

    /**
     * Layout manager referansı
     */
    getLayoutManager(): LayoutManager {
        return this.layoutManager;
    }

    /**
     * Fallback resolver referansı
     */
    getFallbackResolver(): FallbackResolver {
        return this.fallbackResolver;
    }

    // ====================================================================
    // v2.0.0 - VISUAL SYSTEM API
    // ====================================================================

    /**
     * Settings değişiklik handler'ı
     */
    private onSettingsChanged(event: SettingsChangeEvent): void {
        // Tüm dimension'ları yeni ayarlara göre güncelle
        this.dimensions.forEach(dimension => {
            this.applySettingsToDimension(dimension);
        });

        // Event emit
        this.emit('settingsChanged', event);
    }

    /**
     * Dimension'a mevcut ayarları uygula
     * v2.0.0 - Tam görsel güncelleme desteği
     */
    private applySettingsToDimension(dimension: Dimension): void {
        const s = this.settings.getSettings();
        const normalColor = s.colors.dimensionLine ?? s.colors.normal;
        const extColor = s.colors.extensionLine;
        const lineWidth = s.dimensionLine?.width ?? 1.5;

        // Tüm child'ları tara ve materyalleri güncelle
        dimension.traverse((child: THREE.Object3D) => {
            // LineSegments2 / Line2 için (kalın çizgiler)
            if ((child as any).isLine2 || (child as any).isLineSegments2) {
                const line = child as any;
                if (line.material && 'color' in line.material) {
                    // Extension line mi dimension line mı?
                    const isExtension = child.userData?.isExtensionLine ||
                                       child.name?.includes('ext') ||
                                       child.name?.includes('extension');
                    const color = isExtension ? extColor : normalColor;
                    line.material.color.setHex(color);

                    // Çizgi kalınlığını güncelle
                    if ('linewidth' in line.material) {
                        line.material.linewidth = lineWidth * 0.001;
                    }
                }
            }

            // Normal Line için
            if (child instanceof THREE.Line && !(child as any).isLine2) {
                const mat = child.material as THREE.LineBasicMaterial;
                if (mat && 'color' in mat) {
                    const isExtension = child.userData?.isExtensionLine ||
                                       child.name?.includes('ext');
                    mat.color.setHex(isExtension ? extColor : normalColor);
                }
            }

            // Mesh için (oklar)
            if (child instanceof THREE.Mesh) {
                const mat = child.material as THREE.MeshBasicMaterial;
                if (mat && 'color' in mat && !child.userData?.isLabel) {
                    mat.color.setHex(normalColor);
                }
            }

            // Sprite için (label) - Sadece opacity ayarla
            if (child instanceof THREE.Sprite) {
                const mat = child.material as THREE.SpriteMaterial;
                if (mat) {
                    mat.opacity = s.label?.backgroundOpacity ?? 1;
                }
            }
        });

        // Options'u güncelle (gelecek güncellemeler için)
        if ((dimension as any)._options) {
            const opts = (dimension as any)._options;
            opts.color = normalColor;
            opts.lineWidth = lineWidth;
            opts.fontSize = s.label?.fontSize ?? 12;
            opts.textColor = s.label?.textColor ?? '#000000';
        }

        // Sahneyi güncelle
        if (this.scene?.anaGosterim) {
            this.scene.anaGosterim.ciz?.();
        }
    }

    /**
     * Preset uygula (CATIA, SolidWorks, AutoCAD, FikoYT, Minimal, Custom)
     */
    applyPreset(preset: PresetName): void {
        this.settings.applyPreset(preset);
    }

    /**
     * Mevcut ayarları getir
     */
    getSettings(): ReturnType<DimensionSettings['getSettings']> {
        return this.settings.getSettings();
    }

    /**
     * Ayarları güncelle
     */
    updateSettings(updates: Partial<ReturnType<DimensionSettings['getSettings']>>): void {
        this.settings.updateSettings(updates);
    }

    /**
     * Animasyonlu Linear Dimension oluştur
     */
    createLinearDimensionAnimated(
        refA: GeometryReference,
        refB: GeometryReference,
        options: LinearDimensionOptions = {}
    ): DimensionResult {
        const result = this.createLinearDimension(refA, refB, options);

        if (result.success && result.dimension) {
            const dimension = result.dimension;
            const s = this.settings.getSettings();

            // Animasyon aktifse fade-in uygula
            if (s.animation.enabled && s.animation.fadeIn) {
                this.animator.fadeIn(dimension, s.animation.duration);
            }

            // Smart label placement
            if (s.smartPlacement.enabled) {
                const camera = this.scene?.anaGosterim?.aktifKamera;
                if (camera) {
                    // placeLabel(dimension, preferredPosition?, labelSize?, camera?)
                    this.labelPlacer.placeLabel(dimension, undefined, undefined, camera);
                }
            }
        }

        return result;
    }

    /**
     * Animasyonlu Diameter Dimension oluştur
     */
    createDiameterDimensionAnimated(
        ref: GeometryReference,
        options: DiameterDimensionOptions = {}
    ): DimensionResult {
        const result = this.createDiameterDimension(ref, options);

        if (result.success && result.dimension) {
            const s = this.settings.getSettings();

            if (s.animation.enabled && s.animation.fadeIn) {
                this.animator.fadeIn(result.dimension, s.animation.duration);
            }
        }

        return result;
    }

    /**
     * Animasyonlu Radius Dimension oluştur
     */
    createRadiusDimensionAnimated(
        ref: GeometryReference,
        options: DiameterDimensionOptions = {}
    ): DimensionResult {
        const result = this.createRadiusDimension(ref, options);

        if (result.success && result.dimension) {
            const s = this.settings.getSettings();

            if (s.animation.enabled && s.animation.fadeIn) {
                this.animator.fadeIn(result.dimension, s.animation.duration);
            }
        }

        return result;
    }

    /**
     * Animasyonlu Angular Dimension oluştur
     */
    createAngleDimensionAnimated(
        refA: GeometryReference,
        refB: GeometryReference,
        options: AngularDimensionOptions = {}
    ): DimensionResult {
        const result = this.createAngleDimension(refA, refB, options);

        if (result.success && result.dimension) {
            const s = this.settings.getSettings();

            if (s.animation.enabled && s.animation.fadeIn) {
                this.animator.fadeIn(result.dimension, s.animation.duration);
            }
        }

        return result;
    }

    /**
     * Dimension'ı animasyonlu sil
     */
    removeDimensionAnimated(id: string): Promise<boolean> {
        const dimension = this.dimensions.get(id);
        if (!dimension) return Promise.resolve(false);

        const s = this.settings.getSettings();

        if (s.animation.enabled && s.animation.fadeOut) {
            return new Promise(resolve => {
                this.animator.fadeOut(dimension, s.animation.duration, () => {
                    this.removeDimension(id);
                    resolve(true);
                });
            });
        } else {
            return Promise.resolve(this.removeDimension(id));
        }
    }

    /**
     * Hover efektini başlat
     */
    hoverDimension(id: string): void {
        if (!this.isInteractionEnabled) return;

        const dimension = this.dimensions.get(id);
        if (!dimension || dimension === this.hoveredDimension) return;

        // Önceki hover'ı temizle
        if (this.hoveredDimension) {
            this.unhoverDimension();
        }

        this.hoveredDimension = dimension;
        dimension.state = DimensionState.HOVERED;

        const s = this.settings.getSettings();

        // Glow efekti
        if (s.glow.enabled && s.glow.onHover) {
            this.glowEffect.applyToObject(dimension, s.glow.intensity);
        }

        // Tooltip
        if (s.tooltip.enabled) {
            const value = dimension.userData.value || 0;
            const unit = dimension.userData.unit || 'mm';
            const type = dimension.userData.type || 'Linear';

            this.tooltip.show(
                `${type}: ${value.toFixed(2)} ${unit}`,
                0, 0 // Pozisyon mouse event'ten alınacak
            );
        }

        // Hover animasyonu
        if (s.animation.enabled) {
            this.animator.hoverTransition(dimension, true);
        }

        this.emit('dimensionHovered', { id, dimension });
    }

    /**
     * Hover efektini kaldır
     */
    unhoverDimension(): void {
        if (!this.hoveredDimension) return;

        const dimension = this.hoveredDimension;
        const id = dimension.userData.dimensionId;

        if (dimension.state === DimensionState.HOVERED) {
            dimension.state = DimensionState.NORMAL;
        }

        const s = this.settings.getSettings();

        // Glow efektini kaldır
        if (s.glow.enabled) {
            this.glowEffect.removeFromObject(dimension);
        }

        // Tooltip gizle
        this.tooltip.hide();

        // Hover animasyonu (geri)
        if (s.animation.enabled) {
            this.animator.hoverTransition(dimension, false);
        }

        this.hoveredDimension = null;

        this.emit('dimensionUnhovered', { id, dimension });
    }

    /**
     * Selection glow efekti uygula (override selectDimension)
     */
    selectDimensionWithGlow(id: string): boolean {
        const result = this.selectDimension(id);

        if (result) {
            const dimension = this.dimensions.get(id);
            if (dimension) {
                const s = this.settings.getSettings();

                // Selection glow
                if (s.glow.enabled && s.glow.onSelect) {
                    this.glowEffect.applyToObject(dimension, s.glow.intensity * 1.5);
                }

                // Pulse animasyonu
                if (s.animation.enabled) {
                    this.animator.pulse(dimension, 2);
                }
            }
        }

        return result;
    }

    /**
     * Mouse event'ten dimension hover kontrolü
     */
    handleMouseMove(event: MouseEvent, raycaster: THREE.Raycaster): void {
        if (!this.isInteractionEnabled) return;

        // Raycast ile dimension bul
        const intersects = raycaster.intersectObjects(
            Array.from(this.dimensions.values()),
            true
        );

        if (intersects.length > 0) {
            // En yakın dimension'ı bul
            const hit = intersects[0].object;
            let dimension: Dimension | undefined;

            // Parent chain'de Dimension ara
            let current: THREE.Object3D | null = hit;
            while (current) {
                if (current.userData?.dimensionId) {
                    dimension = this.dimensions.get(current.userData.dimensionId);
                    break;
                }
                current = current.parent;
            }

            if (dimension) {
                this.hoverDimension(dimension.userData.dimensionId);

                // Tooltip pozisyonunu güncelle
                if (this.settings.getSettings().tooltip.enabled) {
                    this.tooltip.show(
                        this.tooltip['content'] || '',
                        event.clientX + 15,
                        event.clientY + 15
                    );
                }
            } else {
                this.unhoverDimension();
            }
        } else {
            this.unhoverDimension();
        }
    }

    /**
     * Interaction'ı etkinleştir/devre dışı bırak
     */
    setInteractionEnabled(enabled: boolean): void {
        this.isInteractionEnabled = enabled;

        if (!enabled) {
            this.unhoverDimension();
            this.tooltip.hide();
        }
    }

    /**
     * Snap indicator göster (drag sırasında)
     */
    showSnapIndicator(position: THREE.Vector3, color?: number): void {
        const s = this.settings.getSettings();
        if (s.animation.enabled) {
            this.animator.snapIndicator(position, color);
        }
    }

    /**
     * Settings'i LocalStorage'a kaydet
     */
    saveSettings(): void {
        this.settings.saveToLocalStorage();
    }

    /**
     * Settings'i LocalStorage'dan yükle
     */
    loadSettings(): void {
        this.settings.loadFromLocalStorage();
    }

    /**
     * Settings'i JSON olarak export et
     */
    exportSettings(): string {
        return this.settings.exportSettings();
    }

    /**
     * Settings'i JSON'dan import et
     */
    importSettings(json: string): boolean {
        return this.settings.importSettings(json);
    }

    /**
     * v2.0.0 sistem referansları
     */
    getAnimator(): DimensionAnimator {
        return this.animator;
    }

    getTooltip(): DimensionTooltip {
        return this.tooltip;
    }

    getLabelPlacer(): SmartLabelPlacer {
        return this.labelPlacer;
    }

    getGlowEffect(): DimensionGlowEffect {
        return this.glowEffect;
    }

    // ====================================================================
    // CLEANUP
    // ====================================================================

    /**
     * Temizlik
     */
    dispose(): void {
        // DraggableSupport'u temizle
        if (this.draggableSupport) {
            this.draggableSupport.detach();
        }

        // v2.0.0 - Visual system cleanup
        if (this.settings) {
            this.settings.removeChangeListener(this.onSettingsChanged.bind(this));
        }

        if (this.tooltip) {
            this.tooltip.hide();
        }

        if (this.glowEffect) {
            this.glowEffect.dispose();
        }

        if (this.labelPlacer) {
            this.labelPlacer.clear();
        }

        this.hoveredDimension = null;

        // Tüm dimension'ları temizle
        this.clearAll();

        // Container'ı kaldır
        if (this.container.parent) {
            this.container.parent.remove(this.container);
        }

        // Event handler'ları temizle
        this.eventHandlers.clear();

        // Observer'ı kaldır
        this.cameraObserver = null;
    }
}
